export const learnDe = {
  meta: {
    title: "Lernen lernen — Lumen",
    description: "Der interaktive Leitfaden, um zu verstehen, wie Gedächtnis, Gehirn und die besten Lernmethoden funktionieren.",
  },
  header: {
    badge: "Leitfaden",
    title: "Lernen lernen",
    subtitlePre: "Du musst nicht mehr lernen. Du musst ",
    subtitleBold: "besser",
    subtitlePost: " lernen. So funktioniert dein Verstand wirklich.",
  },
  science: { heading: "Die Wissenschaft des Lernens" },
  methodsHeading: "Lernmethoden",
  finalTip: {
    title: "Der letzte Tipp",
    body: "Es gibt keine perfekte Methode für alle. Probiere aus, miss den Erfolg, passe an. Dein KI-Tutor hilft dir, für jedes Fach die richtige Methode zu wählen.",
  },
  lvl: { easy: "Einfach", media: "Mittel" },
  badge: { accessible: "Barrierefrei" },
  methodsList: { when: "Wann:" },
  dialog: {
    howTo: "So geht's",
    example: "Praktisches Beispiel",
    adaptationsTitle: "Barrierefreie Varianten",
    adaptationsFor: "Anpassungen für",
    noAdaptations: "Keine spezifische Variante: Die Basismethode ist bereits geeignet.",
  },
  adaptMeta: {
    dsa: { label: "LRS", short: "Legasthenie, Dysorthografie, Dyskalkulie" },
    adhd: { label: "ADHS", short: "Aufmerksamkeit und Hyperaktivität" },
    bes: { label: "SEN", short: "sonderpädagogischer Förderbedarf" },
    focus: { label: "Fokus", short: "Schwierigkeiten, konzentriert zu bleiben" },
  },
  chapters: {
    memory: {
      title: "Wie das Gedächtnis funktioniert",
      body: "Das Gedächtnis ist keine Schublade: Es ist ein Muskel. Jedes Mal, wenn du dich an eine Information ERINNERST — statt sie erneut zu lesen — verankerst du sie tiefer. Das nennt man **Active Recall**.\n\nDie Vergessenskurve (Ebbinghaus) zeigt, dass wir nach 24h nur 30% dessen behalten, was wir passiv gelesen haben. Durch verteiltes Wiederholen (Spaced Repetition) steigt die Behaltensrate auf 80–90%.",
    },
    brain: {
      title: "Wie das Gehirn beim Lernen funktioniert",
      body: "Das Gehirn hat zwei Modi: **fokussiert** und **diffus** (entspannt). Sie abzuwechseln — mit Pausen und Schlaf — ermöglicht es dem Gelernten, sich zu festigen. 8 Stunden am Stück ohne Pausen zu lernen ist kontraproduktiv.",
    },
    mistakes: {
      title: "Häufigste Fehler",
      body: "• **Nochmaliges Lesen** statt lautes Wiederholen.\n• **Passives** Lernen mit farbigen Unterstreichungen, ohne je aktiv abzurufen.\n• Alles im letzten Moment vollstopfen.\n• Zu wenig schlafen: Ohne REM-Schlaf festigt sich das Gedächtnis nicht.",
    },
    notes: {
      title: "Wie man Notizen macht",
      body: "Die **Cornell**-Methode: Teile die Seite in 3 Zonen (Notizen, Schlüsselwörter, Zusammenfassung). Schließe am Ende der Stunde das Buch und fülle die Zusammenfassung aus dem Gedächtnis aus. Sie erhöht die Behaltensrate um bis zu 34%.",
    },
    anxiety: {
      title: "Wie man Prüfungsangst bewältigt",
      body: "Die 4-7-8-Atmung (4s einatmen, 7s anhalten, 8s ausatmen) senkt das Cortisol. Am Tag davor keine neuen Themen lernen: nur **wiederholen**. Selbstvertrauen kommt von Vorbereitung, nicht von Angst.",
    },
    forgetting: {
      title: "Wie man das Vergessen vermeidet",
      body: "Wiederhole ein Thema nach 1 Tag, 3 Tagen, 7 Tagen, 21 Tagen. Jeder Abruf macht es stärker. Das ist das Geheimnis von Medizinstudierenden.",
    },
  },
  methods: {
    feynman: {
      d: "Erkläre es, als würdest du es einem 10-Jährigen erklären.",
      when: "Wenn du ein Konzept wirklich verstehen musst.",
      how: [
        "Wähle ein Konzept und schreibe es oben auf ein Blatt.",
        "Erkläre es laut, als würdest du mit einem 10-Jährigen sprechen.",
        "Notiere, wo du steckenbleibst oder schwierige Wörter benutzt.",
        "Schau nur bei diesen Punkten wieder ins Buch und erkläre dann alles noch einmal von vorn.",
      ],
      example: "Konzept: Fotosynthese.\n\nKindgerechte Erklärung: \"Pflanzen essen Licht. Sie nehmen Wasser aus den Wurzeln und Luft aus den Blättern und kochen mit dem Sonnenlicht Zucker, um zu wachsen. Die Luft, die wir atmen, ist ihr 'Abfall'.\"\n\nTypischer Stolperstein: Du kannst nicht erklären, was Chlorophyll damit zu tun hat. → Du schaust wieder ins Buch, entdeckst, dass es das grüne Molekül ist, das Licht einfängt, und erklärst erneut: \"Chlorophyll ist wie ein Solarpanel im Blatt.\"",
      tip: "Wenn du es nicht einfach erklären kannst, hast du es noch nicht verstanden.",
      adaptations: {
        dsa: [
          "Nimm die Erklärung als Audio auf, statt sie zu schreiben — du kannst sie dir wieder anhören.",
          "Nutze den KI-Tutor der App, um das Konzept in einfacheren Worten neu zu formulieren.",
          "Verwende eine gut lesbare Schriftart und aktiviere den LRS-Modus in den Einstellungen.",
        ],
        adhd: [
          "Ein Konzept nach dem anderen, mit einem sichtbaren 10-Minuten-Timer.",
          "Erkläre beim Gehen oder mit Gestik: Bewegung hilft, den Fokus zu halten.",
          "Sofortige Belohnung am Ende (5 Min Musik, Snack, Pause).",
        ],
        bes: [
          "Nutze Zeichnungen oder Symbole statt schwieriger Wörter.",
          "Bitte einen Freund/Elternteil, dir zuzuhören: Feedback reduziert die Angst.",
          "Teile die Erklärung in 3 Miniteile von je 2 Minuten auf.",
        ],
        focus: [
          "Entferne Ablenkungen: Handy in einem anderen Raum, Benachrichtigungen aus.",
          "Erkläre laut, als würdest du ein Video drehen: Das erhöht die Aufmerksamkeit.",
        ],
      },
    },
    activeRecall: {
      d: "Schließe das Buch. Wiederhole laut.",
      when: "Immer. Es ist Methode Nr. 1.",
      how: [
        "Lies einen Absatz oder eine Seite.",
        "Schließe das Buch.",
        "Wiederhole laut (oder schreibe auf), an alles, woran du dich erinnerst.",
        "Öffne es wieder und korrigiere Fehler.",
      ],
      example: "Geschichte — Kapitel über die Französische Revolution.\n\n1) Lies die Ursachen (1789).\n2) Schließe das Buch und versuche zu sagen: \"Wirtschaftskrise, Ungleichheit zwischen den drei Ständen, Einfluss der Aufklärung, Sturm auf die Bastille am 14. Juli…\"\n3) Öffne es wieder: Du hast die Hungersnot von 1788 vergessen. Markiere sie rot.\n4) Versuche es nach 10 Minuten erneut — diesmal erinnerst du dich.",
      tip: "Wiederlesen ≠ Lernen. Wenn du es nicht abrufen kannst, weißt du es nicht.",
      adaptations: {
        dsa: [
          "Wiederhole laut statt zu schreiben: Das umgeht das anstrengende Lesen und Schreiben.",
          "Nutze Audio-Karteikarten: Nimm die Frage auf und hör sie dir an, bevor du antwortest.",
          "Bitte den KI-Tutor, dir mündliche Fragen zum Gelernten zu stellen.",
        ],
        adhd: [
          "Kurze Sitzungen von 5-10 Minuten, danach das Thema wechseln, um Langeweile zu vermeiden.",
          "Verwandle das Wiederholen in ein Punktespiel mit dir selbst.",
          "Im Stehen oder Gehen: Bewegung hilft beim Abrufen.",
        ],
        bes: [
          "Beginne mit 3 einfachen Fragen, bevor du den Schwierigkeitsgrad erhöhst.",
          "Nutze Leitbilder, um das Erinnern auszulösen (visuelles Gedächtnis).",
        ],
        focus: [
          "Kurzer Timer (10 Min) und ein klares Ziel: \"5 Punkte abrufen\".",
          "Schreibe die Antwort sofort auf, ohne nochmal zu lesen: Das aktiviert den Abruf sofort.",
        ],
      },
    },
    pomodoro: {
      d: "25 Min Fokus + 5 Min Pause.",
      when: "Um mit dem Lernen zu beginnen, ohne zu prokrastinieren.",
      how: [
        "Wähle EINE präzise Aufgabe (nicht 'Geschichte lernen', sondern 'Seiten 45-52 lesen').",
        "25-Minuten-Timer. Handy in einem anderen Raum.",
        "5 Minuten echte Pause (aufstehen, trinken, nach draußen schauen).",
        "Nach 4 Pomodoros: eine lange Pause von 15-30 Min.",
      ],
      example: "15:00 Uhr — Pomodoro 1: Seiten 45-52 Geschichte lesen.\n15:25 Uhr — Pause: in der Küche spazieren, Wasser trinken.\n15:30 Uhr — Pomodoro 2: Buch schließen und Active Recall des Kapitels machen.\n15:55 Uhr — Pause.\n16:00 Uhr — Pomodoro 3: 5 Matheaufgaben lösen.\n16:25 Uhr — Lange Pause, 20 Min.",
      tip: "Nutze den Study-Timer der App → Pomodoro-Modus.",
      adaptations: {
        dsa: [
          "Wechsle während des Pomodoros zwischen 10 Min Lesen + 15 Min mündlicher/visueller Aktivität.",
          "Lies während der Pause nicht: Lass die Augen ruhen (in die Ferne schauen, natürliches Licht).",
        ],
        adhd: [
          "Reduziere auf **15 Min Fokus + 5 Min Pause** — realistischer bei ADHS.",
          "Sichtbarer und hörbarer Timer. Schreibe die Aufgabe auf einen Post-it vor dir.",
          "Wenn du dich ablenken lässt, urteile nicht über dich selbst: Bemerke den Gedanken, kehre zur Aufgabe zurück.",
        ],
        bes: [
          "Beginne mit nur 1 Pomodoro pro Tag und steigere dich allmählich.",
          "Nutze einen analogen Timer oder eine Sanduhr: Die Zeit wird greifbar.",
        ],
        focus: [
          "Flugmodus während der 25 Minuten verpflichtend.",
          "ECHTE Pause: keine sozialen Medien, keine Videos. Nur Bewegung oder Wasser.",
        ],
      },
    },
    spacedRepetition: {
      d: "Wiederholen in wachsenden Zeitabständen.",
      when: "Um dich langfristig zu erinnern.",
      how: [
        "Lerne heute (Tag 0).",
        "Wiederhole nach 1 Tag.",
        "Dann nach 3 Tagen, 7 Tagen, 21 Tagen.",
        "Jedes Mal, wenn du etwas falsch machst, beginne wieder bei 1 Tag.",
      ],
      example: "Englischvokabeln, 20 neue Wörter am Montag.\n\n• Di (Tag 1): Rufe die 20 Wörter ab. Du verfehlst 4 → markiere sie.\n• Do (Tag 3): Rufe alle ab. Verschiebe die 4 verfehlten zurück auf Tag 1.\n• Folgender Mo (Tag 7): Wiederhole nur die stabilen.\n• In 3 Wochen: Letzte Wiederholung, 5 Minuten.\n\nErgebnis: 90% Behaltensrate nach einem Monat, gegenüber 30% bei bloßem Wiederlesen.",
      tip: "Die Karteikarten der App nutzen diese Methode bereits (5 Leitner-Boxen).",
      adaptations: {
        dsa: [
          "Bevorzuge Karteikarten mit Bildern oder Audio, nicht nur Text.",
          "In der App kannst du Sprach-Karteikarten erstellen: Nimm die Antwort per Stimme auf.",
        ],
        adhd: [
          "Max. 10 Karten pro Sitzung: Kleine Decks verhindern Aufgeben.",
          "Wiederhole immer zur gleichen Zeit: Routine hilft mehr als Willenskraft.",
        ],
        bes: [
          "Nutze unterschiedliche Farben pro Fach: Das hilft, die richtigen Karten zu finden.",
          "Bitte um Unterstützung beim Erstellen der ersten Karten, danach wird es selbstständig.",
        ],
        focus: [
          "10 Minuten morgens direkt nach dem Aufwachen: Das Gehirn ist aufnahmefähiger.",
          "Schließe alle anderen Tabs/Apps, bevor du mit dem Deck beginnst.",
        ],
      },
    },
    cornellNotes: {
      d: "Notizen in 3 Zonen: Notizen / Schlüsselwörter / Zusammenfassung.",
      when: "Während des Unterrichts.",
      how: [
        "Teile die Seite in 3 Zonen: linke Spalte (Schlüsselwörter), rechte Spalte (Notizen), unteres Band (Zusammenfassung).",
        "Schreibe während der Stunde nur in die Notizspalte (rechts).",
        "Fülle direkt danach die Schlüsselwörter links aus.",
        "Schreibe am Abend 2-3 Zeilen Zusammenfassung unten.",
      ],
      example: "Biologieseite.\n\n[Rechte Notizen] Die Mitose hat 4 Phasen: Prophase, Metaphase, Anaphase, Telophase…\n[Linke Wörter] Mitose · 4 Phasen · Chromosomen\n[Untere Zusammenfassung] Die Mitose teilt eine Zelle in 2 identische. 4 Phasen. Wird für Wachstum und Reparatur genutzt.",
      tip: "Wenn du die rechte Spalte abdeckst, kannst du die Schlüsselwörter als Karteikarten nutzen.",
      adaptations: {
        dsa: [
          "Schreibe in Blockschrift oder nutze ein Tablet mit gut lesbarer Schrift.",
          "Nimm die Stunde auf (mit Erlaubnis) und fülle die Notizen danach in Ruhe aus.",
          "Nutze in der linken Spalte Symbole/Icons statt Wörter, wo möglich.",
        ],
        adhd: [
          "Konzentriere dich während der Stunde nur auf die rechte Spalte: eine Sache nach der anderen.",
          "Fülle Schlüsselwörter und Zusammenfassung DIREKT nach der Stunde aus, nicht erst am nächsten Tag.",
        ],
        bes: [
          "Vorgedruckte Vorlage mit bereits eingeteilten 3 Zonen: reduziert den Organisationsaufwand.",
          "Zusammenfassung in max. 2 Sätzen: lieber kurz und fertig als lang und halb liegen gelassen.",
        ],
        focus: [
          "Stift immer in der Hand: Schreiben hält die Aufmerksamkeit, auch wenn du nicht alles abschreibst.",
        ],
      },
    },
    blurting: {
      d: "Schreibe alles auf, woran du dich erinnerst, dann vergleiche.",
      when: "Vor einer Prüfung.",
      how: [
        "Wähle ein Thema.",
        "Leeres Blatt. 5-10 Min: Schreibe ALLES auf, woran du dich erinnerst, in beliebiger Reihenfolge.",
        "Öffne das Buch und vergleiche.",
        "Kreise die Lücken rot ein. Das ist deine nächste Lernsitzung.",
      ],
      example: "Thema: Kreislaufsystem.\n\nBlurt: \"Herz, 4 Kammern, rechter und linker Vorhof, Kammern, Arterien transportieren sauerstoffreiches Blut außer der Lungenarterie, Venen…\"\n\nBuch öffnen: Du hast die Trikuspidalklappe und die Rolle der Kapillaren vergessen. Das musst du heute Abend wiederholen.",
      tip: "Blurting macht Lücken in 10 Minuten sichtbar. Mach es am Tag vor einer Prüfung.",
      adaptations: {
        dsa: [
          "Mach das Blurting laut: Nimm Audio auf, statt zu schreiben.",
          "Nutze beim Vergleich mit dem Buch farbige Textmarker für die \"Lücken\".",
        ],
        adhd: [
          "5-Minuten-Timer: Wenn er endet, hör auf, auch wenn du nicht fertig bist.",
          "Ein Thema nach dem anderen. Geh nicht zum nächsten über, bevor du verglichen hast.",
        ],
        bes: [
          "Du kannst Diagramme/Pfeile statt vollständiger Sätze verwenden.",
          "Der erste Blurt ist immer voller Lücken: Das ist NORMAL, kein Versagen.",
        ],
        focus: [
          "Leeres Blatt vor dir, alles andere weit weg. Schreibe in einem Zug, ohne anzuhalten.",
        ],
      },
    },
    mindMapping: {
      d: "Stelle Konzepte visuell dar.",
      when: "Um komplexe Ideen zu verbinden.",
      how: [
        "Schreibe das zentrale Konzept in die Mitte einer Seite.",
        "Verzweige mit 3-5 Hauptästen (Unterthemen).",
        "Jeder Ast teilt sich in Unteräste mit Details, Beispielen, Daten.",
        "Nutze unterschiedliche Farben für Makrobereiche und Pfeile für Verbindungen.",
      ],
      example: "Zentrum: \"Zweiter Weltkrieg\".\n\nÄste: Ursachen · Fronten · Personen · Folgen.\n\n• Ursachen → Versailler Vertrag, Krise von 1929, Aufstieg des Nationalsozialismus.\n• Fronten → Europa, Pazifik, Afrika.\n• Personen → Hitler, Churchill, Roosevelt, Stalin.\n• Folgen → UNO, Kalter Krieg, Entkolonialisierung.\n\nPfeile zwischen 'Krise von 1929' und 'Aufstieg des Nationalsozialismus', um Ursache-Wirkung zu zeigen.",
      tip: "In der App kannst du im Bereich Lernen Mindmaps aus deinen Notizen erstellen.",
      adaptations: {
        dsa: [
          "IDEALE Methode für LRS: nutzt das visuelle Gedächtnis und reduziert Text.",
          "Nutze Farben, Symbole und Zeichnungen statt langer Sätze.",
          "In der App kannst du die Karte mit einem Klick aus deinen Notizen erstellen.",
        ],
        adhd: [
          "Beginne von der Mitte aus, ohne zu viel zu planen: Füge Äste hinzu, während du nachdenkst.",
          "Wenn du dich verlierst, halte alles an und kehre zum zentralen Konzept zurück.",
        ],
        bes: [
          "Kleine Karte, max. 3 Hauptäste. Lieber einfach und klar.",
          "Eine Farbe pro Ast hilft, sich die Verbindungen zu merken.",
        ],
        focus: [
          "Baue die Karte von Hand auf einem großen Blatt: Die Bewegung hält dich wach.",
          "Erstelle eine Karte nach der anderen pro Thema — vermische keine Fächer.",
        ],
      },
    },
  },
  toast: {
    mustLogin: "Du musst angemeldet sein, um zu speichern",
    saved: "Ergebnis in deinem Profil gespeichert ✨",
  },
  quiz: {
    cta: {
      discoverTitle: "Entdecke deine Methode",
      discoverSubtitle: "7 kurze Fragen, um herauszufinden, welche Lernmethode am besten zu dir passt.",
      yourMethod: "Deine Methode",
      recommendedBadge: "Empfohlen",
      savedOn: "gespeichert am {{date}}",
      thenPrefix: " · dann ",
      andWord: " und ",
      viewCard: "Karte ansehen",
      retakeTest: "Test wiederholen",
    },
    dialogTitle: "Entdecke deine Methode",
    questionOf: "Frage {{n}} von {{total}}",
    resultsSubtitle: "Hier sind die für dich am besten geeigneten Methoden",
    noResults: "Keine Ergebnisse — versuche es erneut.",
    recommendedBadge: "Empfohlen",
    retake: "Test wiederholen",
    questions: [
      {
        q: "Wenn du etwas Neues lernst, was hilft dir am meisten, es zu verstehen?",
        options: [
          "Es jemandem (oder mir selbst) laut erklären",
          "Diagramme, Verbindungen und Zeichnungen sehen",
          "Laut wiederholen, ohne ins Buch zu schauen",
        ],
      },
      {
        q: "Wie schwer fällt es dir, mit dem Lernen anzufangen?",
        options: [
          "Sehr, ich prokrastiniere immer",
          "Ziemlich, nach einer Weile komme ich in den Flow",
          "Nicht sehr, ich fange sofort an",
        ],
      },
      {
        q: "Wie machst du dir Notizen im Unterricht?",
        options: [
          "Ich schreibe praktisch alles auf",
          "Nur Schlüsselwörter",
          "Ich höre zu, schreibe zu Hause um",
        ],
      },
      {
        q: "Nach wie langer Zeit vergisst du normalerweise, was du gelernt hast?",
        options: [
          "Nach ein paar Tagen",
          "Nach ein bis zwei Wochen",
          "Ich erinnere mich lange daran",
        ],
      },
      {
        q: "Wie bereitest du dich normalerweise auf eine Prüfung vor?",
        options: [
          "Intensive Wiederholung am Tag davor",
          "Etwas Wiederholung pro Tag während der Woche",
          "Ich lerne alles in der Nacht davor",
        ],
      },
      {
        q: "Welche Aktivität hilft dir am meisten, Informationen zu verankern?",
        options: [
          "Handschriftlich schreiben",
          "Sprechen, erklären",
          "Karten, Diagramme, Farben zeichnen",
        ],
      },
      {
        q: "Welche Art von Fächern belastet dich am meisten?",
        options: [
          "Definitionen, Vokabeln, Formeln",
          "Abstrakte Konzepte, die man verstehen muss",
          "Zusammenhängende Themen (Geschichte, Literatur)",
        ],
      },
    ],
  },
};
